/* eslint-disable  @typescript-eslint/no-explicit-any */
import { useState } from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import TextField from "@mui/material/TextField";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { Alert, AlertColor, Link, Snackbar } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { useDataPersistenceService } from "../../services/backend/DataService";
import * as React from "react";

export interface DeepSearchDialogProps {
  visible: boolean;
  onClose: () => void;
}

export const DeepSearchDialog = ({ visible, onClose }: DeepSearchDialogProps) => {
  const dataPersistenceService = useDataPersistenceService();
  const [searchExpression, setSearchExpression] = useState("");
  const [results, setResults] = useState<string[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [alertMessage, setAlertMessage] = useState<string | undefined>(undefined);
  const [alertSeverity, setAlertSeverity] = useState<AlertColor>("info");

  const alertCloseHandler = (_event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === "clickaway") {
      return;
    }
    setAlertMessage(undefined);
  };

  const handleSearch = async () => {
    const trimmed = searchExpression.trim();
    if (!trimmed || isSearching) {
      return;
    }

    setIsSearching(true);
    setHasSearched(false);
    setResults([]);

    try {
      const response = await dataPersistenceService.scanOnS3(trimmed);
      setResults(response.keys);
      setHasSearched(true);
    } catch (e: any) {
      setAlertMessage(e.debugMessage || "Failed to scan S3 data.");
      setAlertSeverity("error");
    } finally {
      setIsSearching(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearch();
    }
  };

  const handleClose = () => {
    setSearchExpression("");
    setResults([]);
    setHasSearched(false);
    setAlertMessage(undefined);
    onClose();
  };

  return (
    <Dialog open={visible} onClose={handleClose} PaperProps={{ sx: { width: "100%", maxWidth: "60vw!important" } }}>
      <DialogTitle>Deep Search on S3</DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: "8px!important" }}>
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          <TextField
            fullWidth
            label="Search Expression"
            value={searchExpression}
            onChange={(e) => setSearchExpression(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Enter text to search for in S3 data..."
            disabled={isSearching}
            autoFocus
            variant="outlined"
          />
          <Button
            variant="contained"
            startIcon={isSearching ? <CircularProgress size={18} color="inherit" /> : <SearchIcon />}
            onClick={handleSearch}
            disabled={!searchExpression.trim() || isSearching}
            sx={{ minWidth: 120, height: 56 }}
          >
            {isSearching ? "Scanning..." : "Search"}
          </Button>
        </Box>

        {isSearching && (
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", py: 4 }}>
            <CircularProgress size={40} />
            <Typography variant="body2" sx={{ mt: 2, color: "text.secondary" }}>
              Scanning S3 bucket... This may take a while.
            </Typography>
          </Box>
        )}

        {!isSearching && hasSearched && (
          <Box sx={{ maxHeight: "50vh", overflowY: "auto" }}>
            <Typography variant="body2" sx={{ mb: 1, color: "text.secondary" }}>
              {results.length} result{results.length !== 1 ? "s" : ""} found
            </Typography>
            {results.length > 0 ? (
              <List dense sx={{ bgcolor: "background.paper", borderRadius: 1, border: "1px solid", borderColor: "divider" }}>
                {results.map((key) => {
                  // key looks like this: "data/Player/511/l2xb9rr0knm0l.json"
                  const parts = key.split("/");
                  const dataStructure = parts.length > 1 ? parts[1] : "unknown";
                  const idWithExtension = parts.length > 3 ? parts[3] : "unknown";
                  const id = idWithExtension.split(".")[0];
                  return (
                    <ListItem
                      key={key}
                      sx={{ borderBottom: "1px solid", borderColor: "divider", "&:last-child": { borderBottom: "none" } }}
                    >
                      <Link
                        href={`/data-entity-editor?data_structure=${dataStructure}&id=${id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <ListItemText primary={key} primaryTypographyProps={{ fontFamily: "monospace", fontSize: "13px" }} />
                      </Link>
                    </ListItem>
                  );
                })}
              </List>
            ) : (
              <Typography variant="body2" sx={{ color: "text.secondary", fontStyle: "italic" }}>
                No files matched the search expression.
              </Typography>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="error">
          Close
        </Button>
      </DialogActions>
      <Snackbar open={alertMessage !== undefined} autoHideDuration={5000} onClose={alertCloseHandler}>
        <Alert onClose={alertCloseHandler} severity={alertSeverity} sx={{ width: "100%" }}>
          {alertMessage}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};
